/**
 * Project-specific utility functions
 */

import type { Category } from "@/contexts/CategoriesContext";
import type { Assignment, ProjectSummary } from "@/hooks/useProjectBoardData";
import { addWeeks, computeProjectEndDate, parseISODate, dateDeltaDays } from "./dateUtils";
import { type ProjectAllocation, type WeekAllocationEntry } from "./makersUtils";
import type { WeekColumn } from "./weekUtils";
import {
  computeWeekColumns,
  getStartOfWeek
} from "./weekUtils";

/**
 * Compose a project's display name, prefixing the client when present.
 *
 * - Treats null/undefined/whitespace-only client as empty (no prefix).
 * - Falls back to "Unassigned" when name is null/undefined.
 */
export function formatProjectDisplayName(
  name: string | null | undefined,
  client: string | null | undefined,
): string {
  const trimmedClient = client?.trim();
  const resolvedName = name ?? "Unassigned";
  return trimmedClient ? `${trimmedClient} - ${resolvedName}` : resolvedName;
}

/**
 * A project belongs on the archive page (and therefore not on the main projects board)
 * when its deal stage is the reserved "lost" stage or its computed end date is before today.
 */
export function isArchivedProject(
  project: { deal_stage?: { kind: string | null } | null; start_date: string; num_weeks: number },
  today = new Date().toISOString().split("T")[0]!,
): boolean {
  if (project.deal_stage?.kind === "lost") return true;
  const endDate = computeProjectEndDate(project.start_date, project.num_weeks);
  return endDate < today;
}

/**
 * Whether the project-level "Clear all assignments" control is offered.
 *
 * Deliberately broader than ProjectRow's `isLockedProject`, which additionally
 * requires likelihood_to_close === 100 and a start date in the past: a sold
 * project that has not started yet must still be protected from a one-click
 * bulk wipe, so the deal stage kind alone decides here. Do not fold this back
 * into isLockedProject.
 *
 * Hidden at 0 or 1 role because the per-row clear button already does the job
 * and there is no bulk to speak of.
 */
export function canClearAllAssignments(project: {
  deal_stage?: { kind: string | null } | null;
  project_assignments: unknown[];
}): boolean {
  if (project.deal_stage?.kind === "won") return false;
  return project.project_assignments.length > 1;
}


/** Shape returned by the `update_project_with_reindex` RPC (jsonb). */
export interface ProjectReindexResult {
  old: Record<string, unknown>;
  new: Record<string, unknown>;
  /** The child assignments the RPC actually resized. */
  resized: Array<{ id: string; old: number[]; new: number[] }>;
}

/** Shape returned by the `update_assignment_with_employees` RPC (jsonb). */
export interface AssignmentEmployeesResult {
  old: Record<string, unknown>;
  new: Record<string, unknown>;
  old_employee_ids: string[];
  new_employee_ids: string[];
}

/**
 * Resize an assignment's weekly_hours_allocated to match a new project num_weeks.
 * - If newNumWeeks is smaller: truncate the array.
 * - If newNumWeeks is larger: pad with the current last value (or 0 if empty).
 */
export function resizeWeeklyHoursAllocation(
  current: number[],
  newNumWeeks: number
): number[] {
  if (newNumWeeks <= 0) return [];
  if (current.length === newNumWeeks) return [...current];
  if (current.length > newNumWeeks) return current.slice(0, newNumWeeks);
  const last = current.length > 0 ? current[current.length - 1] : 0;
  return [...current, ...Array(newNumWeeks - current.length).fill(last)];
}

/**
 * Result of adjusting weekly hours when project dates change
 */
export interface AdjustedWeeklyHours {
  /** The new weekly_hours_allocated array for the assignment */
  weeklyHoursAllocated: number[];
  /** Whether hours were shifted (start date changed) */
  wasShifted: boolean;
  /** Whether hours were padded/trimmed (num_weeks changed) */
  wasPaddedOrTrimmed: boolean;
  /** Number of weeks shifted (positive = forward, negative = backward) */
  weeksDelta: number;
}

/**
 * Adjusts an assignment's weekly hours array when project start_date or num_weeks changes.
 *
 * Business logic:
 * - Start date changes do NOT affect the hours array. Hours are relative to
 *   the project (index 0 = project week 1), so moving the start date just
 *   changes which calendar week each index maps to.
 * - If num_weeks changes: pad or trim the hours array
 *   - Increasing weeks: append zeros to the end
 *   - Decreasing weeks: trim from the end
 *
 * @param currentHours - Current weekly_hours_allocated array
 * @param oldStartDate - Previous project start date (ISO string)
 * @param newStartDate - New project start date (ISO string)
 * @param oldNumWeeks - Previous number of weeks
 * @param newNumWeeks - New number of weeks
 * @returns Adjusted weekly hours result
 */
function adjustWeeklyHoursForDateChange(
  currentHours: number[],
  oldStartDate: string,
  newStartDate: string,
  oldNumWeeks: number,
  newNumWeeks: number
): AdjustedWeeklyHours {
  const DAYS_PER_WEEK = 7;

  // Calculate how many weeks the start date shifted (for reporting only)
  const daysDelta = dateDeltaDays(oldStartDate, newStartDate);
  const weeksDelta = Math.round(daysDelta / DAYS_PER_WEEK);

  // Start with current hours; resize only if num_weeks changed
  const adjustedHours = resizeWeeklyHoursAllocation(currentHours, newNumWeeks);

  return {
    weeklyHoursAllocated: adjustedHours,
    wasShifted: weeksDelta !== 0,
    wasPaddedOrTrimmed: oldNumWeeks !== newNumWeeks,
    weeksDelta,
  };
}

/**
 * Adjusts all assignments' weekly hours when project dates change.
 * 
 * @param assignments - Array of assignments with weekly_hours_allocated
 * @param oldStartDate - Previous project start date
 * @param newStartDate - New project start date
 * @param oldNumWeeks - Previous number of weeks
 * @param newNumWeeks - New number of weeks
 * @returns Map of assignment ID to new weekly_hours_allocated array
 */
export function adjustAllAssignmentsForDateChange(
  assignments: Array<{ id: string; weekly_hours_allocated: number[] | null }>,
  oldStartDate: string,
  newStartDate: string,
  oldNumWeeks: number,
  newNumWeeks: number
): Map<string, number[]> {
  const result = new Map<string, number[]>();
  
  for (const assignment of assignments) {
    const currentHours = assignment.weekly_hours_allocated ?? [];
    const adjusted = adjustWeeklyHoursForDateChange(
      currentHours,
      oldStartDate,
      newStartDate,
      oldNumWeeks,
      newNumWeeks
    );
    result.set(assignment.id, adjusted.weeklyHoursAllocated);
  }
  
  return result;
}

export function resolveAssignmentEnd(assignment: Assignment, project: ProjectSummary): Date {
  return addWeeks(parseISODate(project.start_date), project.num_weeks);
}

export function resolveProjectEnd(project: ProjectSummary): Date {
  return addWeeks(parseISODate(project.start_date), Math.max(project.num_weeks - 1, 0));
}

/**
 * Find the index of the week that contains a date
 */
export function findWeekIndex(
  weeks: WeekColumn[],
  date: Date,
  _inclusive = false
): number {
  if (weeks.length === 0) {
    return -1;
  }

  const exactIndex = weeks.findIndex(
    (week) => date >= week.start && date <= week.end
  );
  if (exactIndex !== -1) {
    return exactIndex;
  }

  if (weeks.length > 0) {
    const firstWeek = weeks[0];
    const lastWeek = weeks[weeks.length - 1];
    if (firstWeek && date < firstWeek.start) {
      return 0;
    }
    if (lastWeek && date > lastWeek.end) {
      return weeks.length - 1;
    }
  }

  return -1;
}

/**
 * Check if a project status is closed
 */
export function filterProjectsForBoard(
  projects: ProjectSummary[],
  searchTerms: string[],
  selectedOffices: string[],
  focusedProjectId?: string | null
): ProjectSummary[] {
  const normalizedTerms = searchTerms
    .map((term) => term.toLowerCase().trim())
    .filter((term) => term.length > 0);

  return projects.filter((project) => {
    // Always include the focused project from URL navigation
    if (focusedProjectId && project.id === focusedProjectId) {
      return true;
    }

    if (normalizedTerms.length > 0) {
      const haystack = [project.name, project.client ?? "", project.office?.name ?? ""]
        .join(" ")
        .toLowerCase();
      if (!normalizedTerms.some((term) => haystack.includes(term))) {
        return false;
      }
    }

    if (selectedOffices.length > 0 && selectedOffices[0] !== "all") {
      if (!selectedOffices.includes(project.office_id)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * A deal-stage name. Deal stages are configurable (DB-backed); this alias is
 * kept for callers that pass a stage by name (e.g. the board's default-stage
 * props). The full stage object lives in DealStagesContext.
 */
export type DealStage = string;

/**
 * Sort projects by how advanced their deal stage is — the stage's configured
 * likelihood_to_close (descending) — then by the project's own likelihood
 * (descending). The stage likelihood is embedded on each project's deal_stage.
 */
function sortProjectsByDealStageAndLikelihood(a: ProjectSummary, b: ProjectSummary): number {
  const stageLikelihoodA = a.deal_stage?.likelihood_to_close ?? -1;
  const stageLikelihoodB = b.deal_stage?.likelihood_to_close ?? -1;

  if (stageLikelihoodA !== stageLikelihoodB) {
    return stageLikelihoodB - stageLikelihoodA;
  }

  const likelihoodA = a.likelihood_to_close ?? 0;
  const likelihoodB = b.likelihood_to_close ?? 0;

  return likelihoodB - likelihoodA;
}

/** A category and the board-visible projects that belong to it, sorted for display. */
export interface CategorizedSection {
  category: Category;
  projects: ProjectSummary[];
}

/**
 * Result of categorizing the board: one section per configured category (ordered
 * by the categories list, i.e. sort_order), plus the projects dropped from the
 * board entirely.
 */
export interface CategorizedBoard {
  sections: CategorizedSection[];
  hidden: ProjectSummary[];
}

/**
 * Bucket projects into category sections by `category_id`, ordered by the
 * provided `categories` (sort_order). Archived projects (ended, or in the
 * reserved "lost" stage) drop to `hidden` unless they're the focused project
 * (deep-linked via ?project=ID), in which case they remain in their own section.
 */
export function categorizeProjects(
  projects: ProjectSummary[],
  categories: Category[],
  focusedProjectId?: string | null,
  today?: string,
): CategorizedBoard {
  const buckets = new Map<string, ProjectSummary[]>(categories.map((c) => [c.id, []]));
  const hidden: ProjectSummary[] = [];

  for (const project of projects) {
    const isFocused = focusedProjectId != null && project.id === focusedProjectId;

    if (isArchivedProject(project, today) && !isFocused) {
      hidden.push(project);
      continue;
    }

    const bucket = project.category_id != null ? buckets.get(project.category_id) : undefined;
    // A missing/unknown category shouldn't happen (FK + NOT NULL default), but if
    // it does the project drops off the board rather than vanishing silently.
    (bucket ?? hidden).push(project);
  }

  const sections = categories.map((category) => ({
    category,
    projects: buckets.get(category.id)!.sort(sortProjectsByDealStageAndLikelihood),
  }));

  return { sections, hidden };
}

function collectEmployeeData(
  projects: ProjectSummary[],
): {
  employeeAssignments: Map<string, Array<{ projectId: string; projectStartDate: string; projectNumWeeks: number; hoursArray: number[] }>>;
  employeeCapacity: Map<string, number>;
  numWeeks: number;
} {
  const employeeAssignments = new Map<
    string,
    Array<{ projectId: string; projectStartDate: string; projectNumWeeks: number; hoursArray: number[] }>
  >();
  const employeeCapacity = new Map<string, number>();
  let numWeeks = 0;

  for (const project of projects) {
    numWeeks = Math.max(numWeeks, project.num_weeks);

    for (const assignment of project.project_assignments) {
      const hoursArray = assignment.weekly_hours_allocated ?? [];
      for (const employee of assignment.employees) {
        if (!employeeAssignments.has(employee.id)) {
          employeeAssignments.set(employee.id, []);
        }
        employeeAssignments.get(employee.id)!.push({
          projectId: project.id,
          projectStartDate: project.start_date,
          projectNumWeeks: project.num_weeks,
          hoursArray,
        });
        if (
          employee.weekly_capacity_hours != null &&
          !employeeCapacity.has(employee.id)
        ) {
          employeeCapacity.set(employee.id, employee.weekly_capacity_hours);
        }
      }
    }
  }

  return { employeeAssignments, employeeCapacity, numWeeks };
}

function getHoursForWeekFromAssignment(
  week: WeekColumn,
  projectStartDate: string,
  projectNumWeeks: number,
  hoursArray: number[]
): number {
  const projectStart = parseISODate(projectStartDate);
  const projectStartWeek = getStartOfWeek(projectStart);
  const weekStart = getStartOfWeek(week.start);

  const diffMs = weekStart.getTime() - projectStartWeek.getTime();
  const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));

  if (diffWeeks < 0 || diffWeeks >= projectNumWeeks || diffWeeks >= hoursArray.length) {
    return 0;
  }

  return hoursArray[diffWeeks] ?? 0;
}

function calculateWeekHours(
  week: WeekColumn,
  assignments: Array<{ projectId: string; projectStartDate: string; projectNumWeeks: number; hoursArray: number[] }>
): number {
  return assignments
    .map((assignment) => getHoursForWeekFromAssignment(week, assignment.projectStartDate, assignment.projectNumWeeks, assignment.hoursArray))
    .reduce((total, hours) => total + hours, 0);
}

export function computeOverAllocatedEmployeeIds(
  projects: ProjectSummary[],
  startDate = new Date(),
): Set<string> {
  const { employeeAssignments, employeeCapacity, numWeeks } =
    collectEmployeeData(projects);

  const weeks = computeWeekColumns(startDate, numWeeks);

  const overAllocated = new Set<string>();
  for (const [empId, assignments] of employeeAssignments) {
    const capacity = employeeCapacity.get(empId) ?? 40;

    for (const week of weeks) {
      const totalHours = calculateWeekHours(week, assignments);
      if (totalHours > capacity) {
        overAllocated.add(empId);
        break;
      }
    }
  }
  return overAllocated;
}

/**
 * For each employee, the list of "active and upcoming" (non-archived) projects
 * they are currently allocated to, formatted as "client - name".
 *
 * Only projects where the employee has positive allocated hours within the
 * current/upcoming week window are included, and each project appears once per
 * employee. The week window matches computeOverAllocatedEmployeeIds so the
 * tooltip explanation never lists projects that don't contribute to the
 * over-allocation (e.g. projects whose hours fall entirely in past weeks).
 */
export function computeEmployeeActiveProjectLabels(
  projects: ProjectSummary[],
  startDate = new Date(),
  today?: string,
): Map<string, string[]> {
  // employeeId -> (projectId -> "client - name"), keyed by project to dedupe
  // across multiple roles on the same project.
  const byEmployee = new Map<string, Map<string, string>>();

  const numWeeks = projects.reduce(
    (max, project) => Math.max(max, project.num_weeks),
    0
  );
  const weeks = computeWeekColumns(startDate, numWeeks);

  for (const project of projects) {
    if (isArchivedProject(project, today)) continue;

    for (const assignment of project.project_assignments) {
      const hoursArray = assignment.weekly_hours_allocated ?? [];
      const windowedHours = weeks.reduce(
        (sum, week) =>
          sum +
          getHoursForWeekFromAssignment(
            week,
            project.start_date,
            project.num_weeks,
            hoursArray
          ),
        0
      );
      if (windowedHours <= 0) continue;

      for (const employee of assignment.employees) {
        let projectLabels = byEmployee.get(employee.id);
        if (!projectLabels) {
          projectLabels = new Map();
          byEmployee.set(employee.id, projectLabels);
        }
        if (!projectLabels.has(project.id)) {
          projectLabels.set(project.id, formatProjectDisplayName(project.name, project.client));
        }
      }
    }
  }

  const result = new Map<string, string[]>();
  for (const [employeeId, projectLabels] of byEmployee) {
    result.set(employeeId, Array.from(projectLabels.values()));
  }
  return result;
}

export function getWeekKey(week: WeekColumn): number {
  return week.start.getTime();
}

export function getProjectWeekIndex(
  weekKey: number,
  weeks: WeekColumn[],
  projectStartDate: string,
  numWeeks: number
): number | null {
  const timelineWeek = weeks.find(w => w.start.getTime() === weekKey);
  if (!timelineWeek) return null;

  const projectStart = new Date(projectStartDate);
  const projectStartWeek = getStartOfWeek(projectStart);
  const timelineWeekStart = getStartOfWeek(timelineWeek.start);

  const diffMs = timelineWeekStart.getTime() - projectStartWeek.getTime();
  const diffWeeks = Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));

  if (diffWeeks < 0 || diffWeeks >= numWeeks) return null;
  return diffWeeks;
}

function getWeekAllocationEntry(
  projectAlloc: ProjectAllocation,
  weekIndex: number,
  weekKey: number,
  fallbackIndex: Map<number, WeekAllocationEntry>
): WeekAllocationEntry | undefined {
  const direct = projectAlloc.weekAllocations[weekIndex];
  if (direct && getWeekKey(direct.week) === weekKey) {
    return direct;
  }
  return fallbackIndex.get(weekKey);
}

function calculateHeightPercentage(hours: number, denominator: number): number {
  if (denominator <= 0) return 0;
  const percentage = (hours / denominator) * 100;

  if (percentage <= 0) return 0;

  const MIN_HEIGHT_PERCENT = 10;
  if (percentage < MIN_HEIGHT_PERCENT) {
    return MIN_HEIGHT_PERCENT;
  }

  return Math.min(percentage, 100);
}

function hasProjectBoardEmployeesField(value: unknown): value is { employees: unknown[] } {
  if (!value || typeof value !== "object") return false;
  if (!("employees" in value)) return false;
  return Array.isArray((value as { employees?: unknown }).employees);
}

/**
 * True when an allocation represents an Unassigned project-board role: it
 * carries project-board assignment records (which have an `employees` array)
 * and none of them has an assigned employee. Makers-board allocations don't
 * carry that field (they use `assignment_employees`), so they're never treated
 * as unassigned here. Used only to render the fill lighter, not to hide it.
 */
function isUnassignedProjectAllocation(projectAlloc: ProjectAllocation): boolean {
  const assignments = (projectAlloc.assignments ?? []) as unknown[];
  const projectBoardAssignments = assignments.filter(hasProjectBoardEmployeesField);
  if (projectBoardAssignments.length === 0) return false;
  return projectBoardAssignments.every((assignment) => assignment.employees.length === 0);
}

/**
 * Standard full-time weekly capacity. Used as the fallback reference when an
 * actual capacity isn't available (mirrors the `?? 40` default the board-data
 * hooks apply to employees with no recorded capacity).
 */
export const DEFAULT_WEEKLY_CAPACITY_HOURS = 40;

/**
 * The weekly capacity a project-board role's timeline fill is measured against.
 * For a staffed role it's the average weekly capacity of the assigned
 * employees. An *unassigned* role has no employee capacity, so it falls back to
 * a full-time week: without a positive reference the fill denominator in
 * `buildTimelineCells` collapses to the role's own hours, painting every
 * unassigned week as a full block regardless of how many hours are allocated.
 */
export function assignmentFillCapacityHours(
  employees: ReadonlyArray<{ weekly_capacity_hours: number }>,
  defaultCapacityHours: number = DEFAULT_WEEKLY_CAPACITY_HOURS
): number {
  if (employees.length === 0) return defaultCapacityHours;
  const total = employees.reduce((sum, emp) => sum + emp.weekly_capacity_hours, 0);
  return total / employees.length;
}

export function buildTimelineCells(
  weeks: WeekColumn[],
  projectAllocations: ProjectAllocation[],
  weeklyCapacityHours: number
): Array<{
  week: WeekColumn;
  key: number;
  hasAllocation: boolean;
  totalHours: number;
  fills: Array<{ projectId: string | null; heightPercentage: number; bottomPercentage: number; unassigned: boolean }>;
}> {
  const allocationIndexes = projectAllocations.map((projectAlloc) => {
    return new Map(projectAlloc.weekAllocations.map((entry) => [getWeekKey(entry.week), entry] as const));
  });

  return weeks.map((week) => {
    const weekKey = getWeekKey(week);
    const allocations = projectAllocations
      .map((projectAlloc, index) => {
        const entry = getWeekAllocationEntry(projectAlloc, index, weekKey, allocationIndexes[index]!);
        if (!entry || !entry.allocation.hasAllocation) return null;
        return {
          projectId: projectAlloc.projectId,
          hours: entry.allocation.totalHours,
          unassigned: isUnassignedProjectAllocation(projectAlloc),
        };
      })
      .filter((alloc): alloc is { projectId: string | null; hours: number; unassigned: boolean } => alloc !== null)
      .sort((a, b) => b.hours - a.hours);

    const totalHours = allocations.reduce((sum, alloc) => sum + alloc.hours, 0);
    const hasAllocation = allocations.length > 0;

    // Any allocation with hours gets a colored fill, including Unassigned role
    // rows (assignments with no assigned employee). Hours now live in a hover
    // tooltip rather than being painted in the cell, so an unfilled fill would
    // read as an empty/0-hour week rather than as unstaffed-but-planned work.
    // Unassigned fills are drawn lighter (see the --unassigned class) to keep
    // them visually distinct from staffed work.
    const denominator = totalHours > weeklyCapacityHours ? totalHours : weeklyCapacityHours;

    let cumulativeBottom = 0;
    const fills = allocations.map((alloc) => {
      const heightPercentage = calculateHeightPercentage(alloc.hours, denominator);
      const fill = {
        projectId: alloc.projectId,
        heightPercentage,
        bottomPercentage: cumulativeBottom,
        unassigned: alloc.unassigned,
      };
      cumulativeBottom += heightPercentage;
      return fill;
    });

    return {
      key: weekKey,
      week: week,
      hasAllocation,
      totalHours,
      fills,
    };
  });
}
